<script setup lang="ts">
import { computed, ref, useTemplateRef, watch } from 'vue'

import StatusMessage from '@tix/ds/components/atoms/StatusMessage.vue'
import type { Cart } from '@tix/entity'

import { type BillingDetailsResult, billingDetails } from '@/helpers/identityForm/billingDetails'
import { buildStripeReturnUrl } from '@/helpers/paymentReturn'

import { errorTracker } from '@/services/errorTracking'

import CaptchaWidget from '@/components/CaptchaWidget.vue'
import StripePayment, { type StripePaymentProps } from '@/components/donations/StripePayment.vue'
import type {
  GuestCheckoutContext,
  IdentityCheckoutData,
  ValidatedIdentityResult,
} from '@/components/donations/checkout.types'

type PaymentComponentProps = {
  cart: Cart
  /** Payment-return destination paths. Cart id and receipt email are layered at confirm. */
  returnUrl: URL
  /** Invoked at Pay for Stripe billing. Pure form data with no side effects. */
  getValidatedIdentity: () => ValidatedIdentityResult
  /** Invoked after a PaymentMethod exists. Returns identity data after guest upsert / member PATCH.*/
  resolveIdentityData: () => Promise<IdentityCheckoutData>
  /** Invoked before Stripe payment submission. Returns a validation error message. */
  validateForm: () => string | null | undefined
} & Pick<StripePaymentProps, 'checkoutPreflight' | 'paymentButtonLabel' | 'payButtonIcon' | 'checkoutCart' | 'pending'>

const props = defineProps<PaymentComponentProps>()

const emit = defineEmits<{
  (e: 'complete', data: { email: string; order: string; amount: number }): void
  (e: 'focus'): void
  (e: 'update:submitting', value: boolean): void
}>()

const captcha = useTemplateRef<InstanceType<typeof CaptchaWidget>>('captcha')

const fee = computed(() => props.cart.stripeFee)

/**
 * Stripe minor units. `0` when there is no outstanding balance (free cart).
 * `null` when `total_outstanding` cannot be parsed (NaN must not be treated as free).
 */
const amount = computed((): number | null => {
  const outstanding = fee.value?.grand_totals.total_outstanding
  const minor = Math.round(Number(outstanding) * 100)
  return Number.isFinite(minor) ? minor : null
})

/** Remount key for {@link StripePayment} so Reload recovers from terminal composable errors. */
const stripeMountKey = ref(0)

/** Narrowed Stripe PMC fields when the cart is booked for a paid checkout. */
const stripeConfig = computed(() => {
  const { pmc_id, account_id, currency_code } = fee.value ?? {}

  if (pmc_id == null || account_id == null || currency_code == null) return null

  return { pmcId: pmc_id, accountId: account_id, currencyCode: currency_code }
})

const preflightError = computed(() => {
  if (amount.value == null) return new Error('Unable to determine cart balance')

  // Free checkout does not need a Stripe PMC / connected account.
  if (amount.value === 0) return null

  if (!stripeConfig.value) return new Error('Cart not booked — no Stripe PMC available')

  return null
})

watch(
  preflightError,
  (error, previous) => {
    if (error && error.message !== previous?.message) {
      errorTracker.track(error, { operation: 'payment.preflightError' })
    }
  },
  { immediate: true },
)

function getBillingDetails(): BillingDetailsResult {
  const result = props.getValidatedIdentity()
  if (result.status === 'error') return result
  return { status: 'success', data: billingDetails(result.data) }
}

async function resolveGuestContext(): Promise<GuestCheckoutContext> {
  try {
    const [identity, captchaResult] = await Promise.all([props.resolveIdentityData(), captcha.value?.getToken()])

    if (identity.status === 'error') return identity
    if (captchaResult?.status === 'error') return captchaResult

    return {
      ...identity,
      returnUrl: buildStripeReturnUrl({
        returnUrl: props.returnUrl,
        cartId: props.cart.id,
        receiptEmail: identity.receiptEmail,
      }),
      ...(captchaResult?.token ? { captcha: { provider: 'hcaptcha' as const, token: captchaResult.token } } : {}),
    }
  } catch (e) {
    return { status: 'error', error: e instanceof Error ? e : new Error(String(e)) }
  }
}
</script>

<template>
  <div class="payment-component">
    <StatusMessage v-if="preflightError" :dismissible="false" :message="preflightError.message" variant="error" />

    <StatusMessage
      v-else-if="amount === 0"
      :dismissible="false"
      message="Free checkout not yet supported"
      variant="error"
    />

    <StripePayment
      v-else-if="stripeConfig"
      :key="stripeMountKey"
      :accountId="stripeConfig.accountId"
      :amount="amount"
      :checkoutCart="checkoutCart"
      :checkoutPreflight="checkoutPreflight"
      :currencyCode="stripeConfig.currencyCode"
      :getBillingDetails="getBillingDetails"
      :payButtonIcon="payButtonIcon"
      :paymentButtonLabel="paymentButtonLabel"
      :pending="pending"
      :pmcId="stripeConfig.pmcId"
      :resolveGuestContext="resolveGuestContext"
      :validateForm="validateForm"
      @complete="emit('complete', $event)"
      @focus="emit('focus')"
      @reload="stripeMountKey++"
      @update:submitting="emit('update:submitting', $event)"
    />

    <CaptchaWidget ref="captcha" />
  </div>
</template>

<style scoped>
.payment-component {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
</style>
